import { useSelector } from "react-redux";
import DataTableBase from "./DataTableBase";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { store } from "../../store";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Home, Store, Inventory2, People, Groups, Category } from "@mui/icons-material";
import { putData } from "../../API/Put";
import updateSnackbarMessage from "../../functions/updateSnackbarMessage";
import fetchResidences from "../../functions/fetchResidences";

export default function ResidencesTable({ filteredResidences, availableZones = [], selectedEstablishment }) {
  const allResidences = useSelector((state) => state.residences);
  const establishments = useSelector((state) => state.establishments);
  const zones = useSelector((state) => state.zones);
  const userDB = useSelector((state) => state.userDB);

  const navigate = useNavigate();

  // État pour le dialog d'affectation de zone
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [selectedZoneForBulk, setSelectedZoneForBulk] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Utiliser les résidences filtrées si fournies, sinon toutes
  const residences = filteredResidences || allResidences;

  // Tri hiérarchique : établissement puis résidence
  const sortedResidences = React.useMemo(() => {
    if (!residences || !establishments) return [];

    return [...residences].sort((a, b) => {
      // Trouver les établissements
      const estabA = establishments.find(e => parseInt(e.id) === parseInt(a.establishment_id));
      const estabB = establishments.find(e => parseInt(e.id) === parseInt(b.establishment_id));

      // Tri par établissement d'abord
      const estabNameA = estabA ? estabA.name : '';
      const estabNameB = estabB ? estabB.name : '';
      const estabCompare = estabNameA.localeCompare(estabNameB);

      if (estabCompare !== 0) {
        return estabCompare; // Différents établissements
      }

      // Même établissement, tri par nom de résidence
      return a.name.localeCompare(b.name);
    });
  }, [residences, establishments]);

  const { t } = useTranslation("common");

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        display: false,
        filter: false,
        sort: true,
      },
    },
    {
      name: "name",
      label: t("common.name"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Home sx={{ color: "#78a882", fontSize: 20 }} />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {value}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "establishment_id",
      label: t("common.establishment"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          let establishment = establishments.find(
            (establishment) => parseInt(establishment.id) === parseInt(value)
          );

          return establishment ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Store sx={{ color: "#6b9dad", fontSize: 20 }} />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {establishment.name}
              </Typography>
            </Box>
          ) : (
            "-"
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            let val1 = obj1.data;
            let val2 = obj2.data;

            let establishmentA = establishments.find(
              (establishment) => parseInt(establishment.id) === parseInt(val1)
            );
            let establishmentB = establishments.find(
              (establishment) => parseInt(establishment.id) === parseInt(val2)
            );

            return (
              (establishmentA?.name || "").localeCompare(establishmentB?.name || "") *
              (order === "asc" ? 1 : -1)
            );
          };
        },
      },
    },
    {
      name: "zone_id",
      label: "Zone",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const zone = zones?.find(z => z.id === value);
          return zone ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: zone.color || "#c4a574",
                }}
              />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {zone.name}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: "#7a8a7a" }}>—</Typography>
          );
        },
        sortCompare: (order) => {
          return (obj1, obj2) => {
            const zoneA = zones?.find(z => z.id === obj1.data);
            const zoneB = zones?.find(z => z.id === obj2.data);
            return (
              (zoneA?.name || "").localeCompare(zoneB?.name || "") *
              (order === "asc" ? 1 : -1)
            );
          };
        },
      },
    },
    {
      name: "pack_id",
      label: t("common.pack"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return value ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Inventory2 sx={{ color: "#c4a574", fontSize: 20 }} />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {value}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ color: "#7a8a7a" }}>—</Typography>
          );
        },
      },
      sortCompare: (order) => {
        return (obj1, obj2) => {
          let val1 = obj1.data;
          let val2 = obj2.data;

          const isNumeric = (value) => {
            return /^-?\d+$/.test(value);
          };

          if (isNumeric(val1) && isNumeric(val2)) {
            return (
              (parseInt(val1) - parseInt(val2)) * (order === "asc" ? 1 : -1)
            );
          } else if (isNumeric(val1)) {
            return -1;
          } else if (isNumeric(val2)) {
            return 1;
          } else {
            return (val1 || "").localeCompare(val2 || "") * (order === "asc" ? 1 : -1);
          }
        };
      },
    },
    {
      name: "current_occupation",
      label: t("common.current_occupation"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const isOccupied = value > 0;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <People sx={{ color: isOccupied ? "#78a882" : "#7a8a7a", fontSize: 20 }} />
              <Typography sx={{ color: isOccupied ? "#ffffff" : "#7a8a7a", fontWeight: 500 }}>
                {value || 0}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "capacity",
      label: t("common.capacity"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Groups sx={{ color: "#8a9ba8", fontSize: 20 }} />
              <Typography sx={{ color: "#ffffff", fontWeight: 500 }}>
                {value || 0}
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  const [selectedResidences, setSelectedresidences] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);

  // Fonction pour affecter une zone en masse
  const handleBulkZoneAssign = async () => {
    if (!selectedZoneForBulk || selectedResidences.length === 0) return;

    setIsAssigning(true);
    let successCount = 0;
    let errorCount = 0;

    // selectedResidences contient des IDs, pas des objets
    for (const residenceId of selectedResidences) {
      try {
        // Trouver la résidence dans les données
        const residence = allResidences.find(r => r.id === residenceId);
        if (!residence) {
          errorCount++;
          continue;
        }

        // Envoyer les données minimales requises avec zone_id
        const response = await putData(`/residences/${residenceId}`, {
          name: residence.name,
          establishment_id: residence.establishment_id,
          current_occupation: residence.current_occupation,
          capacity: residence.capacity,
          zone_id: selectedZoneForBulk === "none" ? null : parseInt(selectedZoneForBulk),
        });
        if (response && (response.status === 200 || response.status === 201)) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    setIsAssigning(false);
    setZoneDialogOpen(false);
    setSelectedZoneForBulk("");

    // Rafraîchir les résidences
    await fetchResidences();

    // Afficher le résultat
    if (errorCount === 0) {
      updateSnackbarMessage(`Zone affectée à ${successCount} installation(s)`);
    } else {
      updateSnackbarMessage(`${successCount} succès, ${errorCount} erreur(s)`);
    }

    // Réinitialiser la sélection
    setSelectedresidences([]);
    setSelectedRows([]);
  };

  // Zones disponibles pour l'affectation (celles de l'établissement ou toutes si pas de filtre)
  const zonesToShow = availableZones.length > 0 ? availableZones : zones || [];

  const tableActions = [
    {
      name: `Affecter zone (${selectedResidences.length})`,
      icon: "Category",
      action: () => {
        setZoneDialogOpen(true);
      },
      disabled: selectedResidences.length === 0,
    },
    {
      name: t("common.delete_selected", {
        name: t("common.residences"),
        count: selectedResidences.length,
      }),
      icon: "Delete",
      action: () => {
        store.dispatch({
          type: "deleteDialog/update",
          payload: {
            open: true,
            type: "residences",
            records: selectedResidences,
          },
        });
      },
    },
  ];

  return (
    <>
      <DataTableBase
        columns={columns}
        data={sortedResidences}
        title={t("common.residences")}
        id="residence-list"
        name="residence-list"
        pageSize={10}
        selectedItems={selectedResidences}
        setSelectedItems={setSelectedresidences}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selectableRows={userDB ? (userDB.admin ? "multiple" : "none") : "none"}
        actions={tableActions}
        sortOrder="dev_eui"
        onRowClick={(rowData, rowMeta) => {
          navigate(`/residences/${sortedResidences[rowMeta.dataIndex].id}`);
        }}
      />

      {/* Dialog pour affecter une zone en masse */}
      <Dialog open={zoneDialogOpen} onClose={() => setZoneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Category sx={{ color: "#c4a574" }} />
          Affecter une zone
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>
            Sélectionnez la zone à affecter aux {selectedResidences.length} installation(s) sélectionnée(s).
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Zone</InputLabel>
            <Select
              value={selectedZoneForBulk}
              label="Zone"
              onChange={(e) => setSelectedZoneForBulk(e.target.value)}
            >
              <MenuItem value="none">
                <em>Aucune zone (retirer)</em>
              </MenuItem>
              {zonesToShow.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: zone.color || "#c4a574",
                      }}
                    />
                    {zone.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoneDialogOpen(false)} disabled={isAssigning}>
            Annuler
          </Button>
          <Button
            onClick={handleBulkZoneAssign}
            variant="contained"
            disabled={!selectedZoneForBulk || isAssigning}
            sx={{ background: "#78a882", "&:hover": { background: "#6a9a74" } }}
          >
            {isAssigning ? "Affectation..." : "Affecter"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
